/**
 * RN Evaluation Note — static options + empty form state.
 * Options mirror the product screens (Eval & Orders → Nurse Evaluation Note).
 */

import { emptyNurseNotesCommonHeader } from "./nurseNotesCommonHeaderUtils";
import { emptyWoundAssessmentValues } from "./woundAssessmentContent";

export const RESIDENCE_TYPE_OPTIONS = [
  "Private Home",
  "Apartment",
  "Assisted Living Facility",
  "Skilled Nursing Facility",
  "Group Home",
  "Homeless",
  "Other",
];

export const LIVES_WITH_OPTIONS = [
  "Alone",
  "Spouse/Partner",
  "Family Member",
  "Caregiver",
  "Roommate",
  "Other",
];

export const HOME_SAFETY_OPTIONS = ["Safe", "Needs Improvement", "Unsafe"];

/** Advance directives — title + short caption (screenshot layout). */
export const ADVANCE_DIRECTIVES = [
  {
    key: "dnr",
    title: "DNR",
    description: "Do Not Resuscitate Order",
  },
  {
    key: "livingWill",
    title: "Living Will",
    description: "Instructional Directive",
  },
  {
    key: "healthCareProxy",
    title: "Health Care Proxy",
    description: "Designated Surrogate",
  },
  {
    key: "powerOfAttorney",
    title: "Power of Attorney",
    description: "Financial & Legal POA",
  },
];

export { PAIN_LEVELS } from "./clinicalVitals";

export const NEURO_OPTIONS = [
  "Alert & Oriented",
  "Confusion/Dementia",
  "Numbness/Tingling",
];

export const CARDIOVASCULAR_OPTIONS = [
  "Regular Rhythm",
  "Peripheral Edema",
  "Chest Pain/Angina",
];

export const RESPIRATORY_OPTIONS = [
  "Lungs Clear Bilaterally",
  "Dyspnea on Exertion",
  "Productive Cough",
];

export const INTEGUMENTARY_OPTIONS = [
  "Skin Intact",
  "Pressure Ulcer Risk",
];

export const GASTROINTESTINAL_OPTIONS = [
  "Active Bowel Sounds",
  "Nausea/Vomiting",
  "Constipation",
];

export const SKIN_INTEGRITY_OPTIONS = [
  "Skin Intact",
  "Rashes/Lesions",
  "Itching/Dryness",
  "Pressure Ulcers",
  "Surgical Wounds",
];

export const GENERAL_SYSTEMS_OPTIONS = [
  "Fatigue",
  "Weakness",
  "Fever/Chills",
  "Sleeping Difficulties",
  "Weight Change",
];

export const APPETITE_OPTIONS = ["Good", "Fair", "Poor"];

export const DIETARY_RESTRICTIONS_OPTIONS = [
  "Low Sodium (2g)",
  "Diabetic (NCS)",
  "Renal Diet",
  "Mechanical Soft",
];

export const PRESCRIBED_DIET_TYPE_OPTIONS = [
  "Standard / Regular",
  "Cardiac",
  "Diabetic",
  "Renal",
  "Low Sodium",
  "Pureed",
  "Mechanical Soft",
  "Liquid",
  "Enteral/Tube Feeding",
  "Other",
];

export const ORIENTATION_OPTIONS = ["Person", "Place", "Time", "Purpose"];

export const SAFETY_AWARENESS_OPTIONS = [
  "Reliable / Independent",
  "Requires Supervision",
  "Impaired",
  "Unable to Assess",
];

export const AMBULATORY_STATUS_OPTIONS = [
  "Independent",
  "Ambulates with Assistive Device",
  "Requires Assistance x1",
  "Requires Assistance x2",
  "Wheelchair Bound",
  "Bedbound",
  "Non-Ambulatory",
];

export const TRANSFER_ABILITY_OPTIONS = ["Bed-to-Chair", "Sit-to-Stand"];

export const ASSISTIVE_DEVICES_OPTIONS = [
  "Cane",
  "Walker",
  "Wheelchair",
  "None",
];

export const NURSING_DIAGNOSES_OPTIONS = [
  "Alteration in comfort",
  "Impaired skin integrity",
  "Risk for infection",
  "Knowledge deficit",
];

export const EDUCATION_TOPICS_OPTIONS = [
  "Disease process",
  "Medication education",
  "Safety / Fall prevention",
  "Signs/symptoms to report",
];

export const ASSESSMENT_UNDERSTANDING_OPTIONS = [
  "Good - Demonstrates mastery",
  "Fair - Requires minor cues",
  "Needs Reinforcement",
];

export const RISK_IDENTIFIERS_OPTIONS = [
  "Fall Risk (Morse 45+)",
  "Diabetic Complications",
  "Medication Non-Compliance",
  "Skin Breakdown",
];

export const NURSING_INTERVENTIONS_OPTIONS = [
  "Med Education",
  "Wound Care",
  "IV Therapy",
  "Lab Draws",
  "Pain Mgt",
  "Cath Care",
  "Pulse Ox Mgt",
  "PT/OT Coord",
];

export const MEDICATION_COMPLIANCE_OPTIONS = ["Compliant", "Inconsistent"];

export const newMedicationRowId = () =>
  `med-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const emptyMedicationRow = () => ({
  id: newMedicationRowId(),
  medicationName: "",
  dosageRoute: "",
  frequency: "",
  compliance: "Compliant",
});

/**
 * Map patient Medication Management rows into the RN Eval reconciliation table.
 * Prefers Active meds; falls back to whatever was returned if status is missing.
 */
export const mapPatientMedicationsToEvalRows = (medications) => {
  const list = Array.isArray(medications) ? medications : [];
  const active = list.filter(
    (med) => !med?.status || String(med.status).toLowerCase() === "active",
  );
  const source = active.length ? active : list;
  return source.map((med) => {
    const dose = med.dosage || med.dose || "";
    const route = med.route || "";
    return {
      id: med.id != null ? `patient-med-${med.id}` : newMedicationRowId(),
      medicationName: med.medicationName || "",
      dosageRoute: [dose, route].filter(Boolean).join(" ").trim() || dose,
      frequency: med.frequency || "",
      compliance: "Compliant",
      sourceMedicationId: med.id ?? null,
    };
  });
};

export const toggleInList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};

const today = () => new Date().toISOString().slice(0, 10);

/** Full editable RN Evaluation Note payload (persisted as JSON on the dedicated row). */
export const emptyRnEvalPayload = () => ({
  billingCode: "",
  timeIn: "",
  timeOut: "",

  commonHeader: emptyNurseNotesCommonHeader(),

  /** Form-specific medical history (diagnoses live in commonHeader). */
  onsetDate: "",
  surgicalDate: "",
  pertinentClinicalHistory: "",

  /** Legacy med rows retained for older payloads; UI uses commonHeader.medications. */
  medications: [],

  residenceType: "",
  livesWith: "",
  homeSafety: "",
  dnr: false,
  livingWill: false,
  healthCareProxy: false,
  powerOfAttorney: false,

  vitalsBp: "",
  vitalsHr: "",
  vitalsRr: "",
  vitalsTemp: "",
  vitalsSpo2: "",
  vitalsWeight: "",
  painLevel: null,

  ...emptyWoundAssessmentValues(),

  neurological: [],
  cardiovascular: [],
  respiratory: [],
  integumentary: [],
  gastrointestinal: [],
  skinIntegrity: [],
  general: [],
  additionalObservations: "",

  appetite: "",
  dietaryRestrictions: [],
  prescribedDietType: "",
  orientation: [],
  safetyAwareness: "",

  ambulatoryStatus: "",
  transferAbility: [],
  assistiveDevices: [],

  nursingDiagnoses: [],
  nursingDiagnosesNarrative: "",

  educationTopicsCovered: [],
  assessmentOfUnderstanding: "",

  nursingImpressions: "",
  riskIdentifiers: [],

  proposedVisitFrequency: "",
  estimatedDischargeDate: "",
  primaryCareCoordinator: "",
  clinicalGoals: "",

  nursingInterventions: [],
});

export { today as rnEvalToday };
